/**
 * Array Control Component
 * Renders array items as table or list
 */

import React, { useState } from 'react';
import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow, IconButton, Typography } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, DragIndicator as DragIcon } from '@mui/icons-material';
import { ControlComponentProps } from '../../../../types/ControlTypes';
import { ArraySettings } from '../../../../types/ControlTypes';
import { LabelWithInfo } from './LabelWithInfo';

export const ArrayControl: React.FC<ControlComponentProps> = ({
  name,
  value,
  onChange,
  error,
  disabled,
  readonly,
  label,
  description,
  required,
  control,
  renderChild,
}) => {
  const settings = control.settings as ArraySettings;
  const items = Array.isArray(value) ? value : [];
  const display = settings?.display || 'table';
  const addButtonLabel = settings?.addButtonLabel || 'Add Item';
  const maxItems = settings?.maxItems;
  const minItems = settings?.minItems || 0;

  // Resolve itemType: supports both explicit itemType and legacy childs-array format
  // childs: [{title, x-control}, ...] → convert to a block schema with keyed childs
  const resolvedItemType: Record<string, any> = React.useMemo(() => {
    if (settings?.itemType) return settings.itemType;
    if (Array.isArray(settings?.childs) && settings.childs.length > 0) {
      const childsObj: Record<string, any> = {};
      (settings.childs as Array<Record<string, any>>).forEach((child, i) => {
        const key = child.title
          ? child.title.toLowerCase().replace(/\s+/g, '_')
          : `field_${i}`;
        childsObj[key] = child;
      });
      return {
        'x-control': {
          type: 'block',
          settings: { variant: 'box', childs: childsObj },
        },
      };
    }
    return {};
  }, [settings?.itemType, settings?.childs]);

  const canAddMore = !maxItems || items.length < maxItems;
  const canRemove = items.length > minItems;

  const handleAddItem = () => {
    const newItem = {};
    const newItems = [...items, newItem];
    onChange(newItems);
  };

  const handleRemoveItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    onChange(newItems);
  };

  const handleItemChange = (index: number, newValue: any) => {
    const newItems = [...items];
    newItems[index] = newValue;
    onChange(newItems);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.setData('draggedIndex', String(index));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    const draggedIndex = parseInt(e.dataTransfer.getData('draggedIndex'));
    if (draggedIndex !== targetIndex) {
      const newItems = [...items];
      [newItems[draggedIndex], newItems[targetIndex]] = [newItems[targetIndex], newItems[draggedIndex]];
      onChange(newItems);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <LabelWithInfo
          label={label || name}
          description={description}
          required={required}
        />
        <Typography variant="caption" sx={{ color: '#999' }}>
          {items.length}/{maxItems || '∞'} items
        </Typography>
      </Box>

      {display === 'table' ? (
        // Table view
        <Box sx={{ overflowX: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                {settings?.allowDragSort && <TableCell width="40px"></TableCell>}
                <TableCell>Item</TableCell>
                <TableCell width="60px" align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow
                  key={index}
                  draggable={settings?.allowDragSort && !readonly}
                  onDragStart={(e) => settings?.allowDragSort && handleDragStart(e, index)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => settings?.allowDragSort && handleDrop(e, index)}
                  sx={{
                    cursor: settings?.allowDragSort ? 'grab' : 'default',
                    opacity: readonly ? 0.6 : 1,
                  }}
                >
                  {settings?.allowDragSort && (
                    <TableCell sx={{ cursor: 'grab', p: 0.5 }}>
                      <DragIcon sx={{ fontSize: '18px', color: '#999' }} />
                    </TableCell>
                  )}
                  <TableCell sx={{ p: 1 }}>
                    {renderChild ? (
                      renderChild({
                        schema: resolvedItemType,
                        value: item,
                        onChange: (newValue) => handleItemChange(index, newValue),
                        fieldPath: `${name}[${index}]`,
                      })
                    ) : (
                      <span>{JSON.stringify(item)}</span>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ p: 0.5 }}>
                    {canRemove && !readonly && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveItem(index)}
                        color="error"
                      >
                        <DeleteIcon sx={{ fontSize: '16px' }} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : (
        // List view
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {items.map((item, index) => (
            <Box
              key={index}
              sx={{
                p: 1,
                border: '1px solid #ddd',
                borderRadius: '4px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box sx={{ flex: 1 }}>
                {renderChild ? (
                  renderChild({
                    schema: resolvedItemType,
                    value: item,
                    onChange: (newValue) => handleItemChange(index, newValue),
                    fieldPath: `${name}[${index}]`,
                  })
                ) : (
                  <span>{JSON.stringify(item)}</span>
                )}
              </Box>
              {canRemove && !readonly && (
                <IconButton
                  size="small"
                  onClick={() => handleRemoveItem(index)}
                  color="error"
                  sx={{ ml: 1 }}
                >
                  <DeleteIcon sx={{ fontSize: '16px' }} />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Add button */}
      {canAddMore && !readonly && (
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddItem}
          size="small"
          variant="outlined"
          sx={{ mt: 1 }}
          disabled={disabled}
        >
          {addButtonLabel}
        </Button>
      )}

      {error && <Box sx={{ color: '#d32f2f', fontSize: '12px', mt: 1 }}>{error}</Box>}
    </Box>
  );
};

export default ArrayControl;
